import { getHolidays, getSchoolDays } from '@bp/shared-planung-utils';
import { Button, ButtonGroup, EditIcon, Table, TableColumns } from '@bp/ui-components';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import { useSelectedSchoolYear } from '../../../hooks/useSelectedSchoolYear';
import { useSchoolYearsQuery } from '../../../types/planung-graphql-client-defs';
import { useTimetableBlocks } from '../hooks/useTimetableBlocks';

type TimetableBlockTableType = {
  uuid: string;
  name: string;
  timeframe: string;
  comment: string | null;
  schoolDays: number;
  schoolDaysPercent: number;
  factor: number;
};

type TimetableBlockTableProps = {
  searchString: string | null;
  onEvaluate: (uuid: string) => void;
  onEdit: (uuid: string) => void;
  readonly: boolean;
};

export const TimetableBlockTable = observer(
  ({ searchString, onEvaluate, onEdit, readonly }: TimetableBlockTableProps) => {
    const { t } = useTranslation();
    const schoolYear = useSelectedSchoolYear();
    const schoolYearContext = useMemorizedCacheTag('SCHOOL_YEAR');

    const { timetableBlocks } = useTimetableBlocks(schoolYear?.uuid, { onlyWithActiveVersion: true });

    const [{ data: schoolYearsData }] = useSchoolYearsQuery({
      context: schoolYearContext,
    });

    const holidays = schoolYearsData?.schoolYears.find((s) => s.uuid === schoolYear?.uuid)?.holidays ?? [];
    const holidaysSet = getHolidays(
      null,
      holidays.map((h) => ({ start: dayjs(h.start), end: dayjs(h.end) })),
    );

    const daysTotal =
      timetableBlocks?.reduce((prev, curr) => {
        const dayCount = getSchoolDays(dayjs(curr.start), dayjs(curr.end), holidaysSet).size;
        return prev + dayCount;
      }, 0) ?? 1;

    const data: TimetableBlockTableType[] =
      timetableBlocks?.map((block) => {
        const days = getSchoolDays(dayjs(block.start), dayjs(block.end), holidaysSet).size;

        return {
          uuid: block.uuid,
          name: block.name,
          timeframe: `${dayjs(block.start).format('DD.MM.YY')} - ${dayjs(block.end).format('DD.MM.YY')}`,
          comment: block.teachingLoadComment ?? null,
          schoolDays: days,
          schoolDaysPercent: Math.round((100 / daysTotal) * days * 100) / 100,
          factor:
            block.teachingLoadFactor || block.teachingLoadFactor === 0
              ? Math.round(block.teachingLoadFactor * 10000) / 100
              : 100,
          calculated: block.teachingLoadCalculated?.toString() ?? '-',
        };
      }) ?? [];

    const columns: TableColumns<TimetableBlockTableType>[] = [
      { header: t('common.name'), id: 'name', accessorKey: 'name', size: 325, isMultiline: true },
      {
        header: t('common.timeFrame'),
        id: 'timeframe',
        accessorKey: 'timeframe',
        size: 160,
        isMultiline: true,
      },
      {
        header: t('common.comment'),
        id: 'comment',
        accessorKey: 'comment',
        size: 400,
        isMultiline: true,
      },
      {
        header: t('timetableBlock.schoolDays'),
        id: 'schoolDays',
        accessorFn: ({ schoolDays, schoolDaysPercent }) => `${schoolDays} (${schoolDaysPercent}%)`,
        size: 100,
      },
      {
        header: t('common.factor'),
        id: 'factor',
        accessorFn: ({ factor }) => `${factor}%`,
        size: 100,
      },
    ];

    const lastCol = (row: TimetableBlockTableType): ReactNode => {
      return (
        <ButtonGroup>
          <Button hierarchy='secondary' onClick={() => onEvaluate(row.uuid)}>
            {t('common.evaluate')}
          </Button>
          <Button
            hierarchy='secondary'
            onClick={() => onEdit(row.uuid)}
            icon={<EditIcon className='small' />}
            disabled={readonly}
          />
        </ButtonGroup>
      );
    };

    return (
      <Table<TimetableBlockTableType>
        data={data}
        columns={columns}
        isOnWhite={false}
        showBorderRadius
        showShadow
        lastColWidth='120px'
        lastCol={({ original }) => lastCol(original)}
        globalFilter={searchString ?? undefined}
      />
    );
  },
);
